import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, BookUser, Building2, Hash, Loader2, Mail, MapPin, MessageSquarePlus, Pencil, Phone, Plus, Save, Search, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateHeure } from "@/components/scans/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { peut } from "@/lib/permissions";

const VIDE = { nom: "", ancienne_adresse: "", nouvelle_adresse: "", telephones: [], emails: [], compte: "", description: "" };

/** Liste modifiable (téléphones, e-mails) : une valeur par ligne, + pour ajouter. */
function ListeValeurs({ label, valeurs, onChange, type = "text", placeholder, testId }) {
  const [saisie, setSaisie] = useState("");
  const ajouter = () => {
    const v = saisie.trim();
    if (!v) return;
    if (!valeurs.includes(v)) onChange([...valeurs, v]);
    setSaisie("");
  };
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {valeurs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {valeurs.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-sm font-mono">
              {v}
              <button type="button" onClick={() => onChange(valeurs.filter((x) => x !== v))} className="text-muted-foreground hover:text-destructive" aria-label={`Retirer ${v}`}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input type={type} value={saisie} onChange={(e) => setSaisie(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ajouter(); } }}
          onBlur={ajouter} data-testid={testId} />
        <Button type="button" variant="outline" size="icon" onClick={ajouter} aria-label="Ajouter"><Plus size={15} /></Button>
      </div>
    </div>
  );
}

/** Formulaire d'une fiche (création et modification). Seul le nom est obligatoire. */
function FicheForm({ open, fiche, onClose, onSaved }) {
  const [f, setF] = useState(VIDE);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(fiche ? { ...VIDE, ...fiche } : VIDE); setBusy(false); } }, [open, fiche]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const enregistrer = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { nom: f.nom, ancienne_adresse: f.ancienne_adresse, nouvelle_adresse: f.nouvelle_adresse,
      telephones: f.telephones, emails: f.emails, compte: f.compte, description: f.description };
    try {
      const { data } = fiche ? await api.put(`/registre/${fiche.id}`, body) : await api.post("/registre", body);
      toast.success(fiche ? "Fiche modifiée" : `Fiche « ${data.nom} » créée`);
      onSaved(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92dvh] overflow-y-auto" data-testid="registre-form">
        <form onSubmit={enregistrer} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{fiche ? `Modifier « ${fiche.nom} »` : "Nouvelle fiche"}</DialogTitle>
            <DialogDescription>Seul le nom est obligatoire : remplissez ce que vous savez.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="rg-nom">Nom de la société ou du client *</Label>
            <Input id="rg-nom" value={f.nom} onChange={(e) => set("nom", e.target.value)} maxLength={200} autoFocus data-testid="rg-nom" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rg-ancienne">Ancienne adresse</Label>
              <Textarea id="rg-ancienne" value={f.ancienne_adresse} onChange={(e) => set("ancienne_adresse", e.target.value)} rows={3} placeholder="N°, rue, code postal, ville" data-testid="rg-ancienne" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rg-nouvelle">Nouvelle adresse</Label>
              <Textarea id="rg-nouvelle" value={f.nouvelle_adresse} onChange={(e) => set("nouvelle_adresse", e.target.value)} rows={3} placeholder="N°, rue, code postal, ville" data-testid="rg-nouvelle" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <ListeValeurs label="Téléphone(s)" valeurs={f.telephones} onChange={(v) => set("telephones", v)} type="tel" placeholder="06 12 34 56 78" testId="rg-tel" />
            <div className="space-y-1.5">
              <Label htmlFor="rg-compte">N° de compte</Label>
              <Input id="rg-compte" value={f.compte} onChange={(e) => set("compte", e.target.value)} maxLength={80} className="font-mono" data-testid="rg-compte" />
            </div>
          </div>
          <ListeValeurs label="E-mail(s)" valeurs={f.emails} onChange={(v) => set("emails", v)} type="email" placeholder="contact@exemple.fr" testId="rg-mail" />
          <div className="space-y-1.5">
            <Label htmlFor="rg-desc">Description (colis, consignes…)</Label>
            <Textarea id="rg-desc" value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="rg-desc" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
            <Button type="submit" disabled={!f.nom.trim() || busy} data-testid="rg-enregistrer">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {fiche ? "Enregistrer" : "Créer la fiche"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Adresse = ({ label, texte, nouvelle }) => (texte ? (
  <div className={`rounded-lg border p-3 ${nouvelle ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}>
    <p className={`text-[11px] uppercase tracking-wide font-semibold ${nouvelle ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}>{label}</p>
    <p className={`text-sm whitespace-pre-line ${nouvelle ? "font-medium" : "text-muted-foreground line-through decoration-muted-foreground/50"}`}>{texte}</p>
  </div>
) : null);

/** Outils › Registre d'informations : recherche + liste des fiches. */
export default function RegistrePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [form, setForm] = useState(false);
  const charger = useCallback(async (texte) => {
    try { const { data: d } = await api.get("/registre", { params: texte.trim() ? { q: texte } : {} }); setData(d); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { const t = setTimeout(() => charger(q), q ? 250 : 0); return () => clearTimeout(t); }, [q, charger]);
  const fiches = data?.fiches || [];
  return (
    <AppShell title="Registre d'informations">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><BookUser size={22} /> Registre d'informations</h1>
          <p className="text-sm text-muted-foreground">Ce que l'on sait des clients : déménagement, nouvelle adresse, téléphone, consignes… · {data ? `${data.total} fiche(s)` : "…"}</p>
        </div>
        {peut(user, "registre_modifier") && <Button onClick={() => setForm(true)} data-testid="btn-nouvelle-fiche"><Plus size={15} /> Nouvelle fiche</Button>}
      </div>
      <div className="relative mb-5">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, société, n° de compte, téléphone, e-mail, adresse…"
          className="h-14 pl-12 text-lg rounded-xl" autoFocus data-testid="registre-recherche" />
        {q && <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Effacer"><X size={18} /></button>}
      </div>
      {!data ? <p className="text-sm text-muted-foreground">Chargement…</p> : fiches.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="registre-vide">
          {q.trim() ? "Aucune fiche ne correspond à la recherche." : "Aucune fiche pour l'instant. Cliquez sur « Nouvelle fiche »."}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="registre-liste">
          {fiches.map((f) => (
            <button key={f.id} type="button" onClick={() => navigate(`/outils/registre/${f.id}`)}
              className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition-colors space-y-2" data-testid={`fiche-${f.nom}`}>
              <p className="font-semibold flex items-center gap-2"><Building2 size={15} className="text-primary shrink-0" /> <span className="truncate">{f.nom}</span></p>
              {f.nouvelle_adresse && <p className="text-sm flex items-start gap-1.5"><MapPin size={13} className="text-emerald-600 mt-0.5 shrink-0" /> <span className="line-clamp-2">{f.nouvelle_adresse}</span></p>}
              {f.ancienne_adresse && <p className="text-xs text-muted-foreground flex items-start gap-1.5"><MapPin size={12} className="mt-0.5 shrink-0" /> <span className="line-clamp-1 line-through">{f.ancienne_adresse}</span></p>}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                {f.telephones?.[0] && <span className="flex items-center gap-1 font-mono"><Phone size={11} /> {f.telephones[0]}</span>}
                {f.compte && <span className="flex items-center gap-1 font-mono"><Hash size={11} /> {f.compte}</span>}
                {f.emails?.[0] && <span className="flex items-center gap-1"><Mail size={11} /> {f.emails[0]}</span>}
              </div>
              <p className="text-[11px] text-muted-foreground">Mise à jour le {fmtDateHeure(f.updated_at)} par {f.updated_by?.nom || "—"}</p>
            </button>
          ))}
        </div>
      )}
      <FicheForm open={form} onClose={() => setForm(false)} onSaved={(d) => { setForm(false); navigate(`/outils/registre/${d.id}`); }} />
    </AppShell>
  );
}

/** Fiche du registre : informations, adresses, journal (informations datées). */
export function RegistreFichePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [edit, setEdit] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const modifier = peut(user, "registre_modifier");
  useEffect(() => { api.get(`/registre/${id}`).then(({ data }) => setF(data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  const ajouterNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/registre/${id}/notes`, { texte: note }); setF(data); setNote(""); toast.success("Information ajoutée"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const supprimerNote = async (n) => {
    try { const { data } = await api.delete(`/registre/${id}/notes/${n.id}`); setF(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const supprimer = async () => {
    try { await api.delete(`/registre/${id}`); toast.success("Fiche supprimée"); navigate("/outils/registre"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AppShell title="Registre d'informations">
      <Link to="/outils/registre" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={15} /> Tout le registre</Link>
      {!f ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
          <section className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="registre-fiche">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="rf-nom"><Building2 size={20} className="text-primary" /> {f.nom}</h1>
                <p className="text-xs text-muted-foreground">Créée le {fmtDateHeure(f.created_at)} par {f.created_by?.nom || "—"} · mise à jour le {fmtDateHeure(f.updated_at)}</p>
              </div>
              <div className="flex gap-2">
                {modifier && <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="rf-modifier"><Pencil size={14} /> Modifier</Button>}
                {peut(user, "registre_supprimer") && (confirmDel
                  ? <Button size="sm" variant="destructive" onClick={supprimer} data-testid="rf-supprimer-ok">Supprimer la fiche ?</Button>
                  : <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDel(true)} aria-label="Supprimer"><Trash2 size={14} /></Button>)}
              </div>
            </div>
            {(f.nouvelle_adresse || f.ancienne_adresse) ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Adresse label="Nouvelle adresse" texte={f.nouvelle_adresse} nouvelle />
                <Adresse label="Ancienne adresse" texte={f.ancienne_adresse} />
              </div>
            ) : <p className="text-sm text-muted-foreground">Pas d'adresse renseignée.</p>}
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 text-sm">
              <div><dt className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={12} /> Téléphone(s)</dt>
                <dd className="font-mono">{f.telephones?.length ? f.telephones.map((t) => <a key={t} href={`tel:${t.replace(/\s/g, "")}`} className="block hover:underline">{t}</a>) : "—"}</dd></div>
              <div><dt className="text-xs text-muted-foreground flex items-center gap-1"><Hash size={12} /> N° de compte</dt><dd className="font-mono">{f.compte || "—"}</dd></div>
              <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground flex items-center gap-1"><Mail size={12} /> E-mail(s)</dt>
                <dd>{f.emails?.length ? f.emails.map((m) => <a key={m} href={`mailto:${m}`} className="block hover:underline">{m}</a>) : "—"}</dd></div>
              <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground">Description</dt><dd className="whitespace-pre-line" data-testid="rf-description">{f.description || "—"}</dd></div>
            </dl>
          </section>
          <aside className="rounded-xl border border-border bg-card p-5 space-y-3 h-fit">
            <h2 className="font-heading font-semibold flex items-center gap-2"><MessageSquarePlus size={16} /> Informations</h2>
            {modifier && (
              <div className="space-y-2">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Ex. colis du 04/11 livré à la nouvelle adresse, client joint par téléphone…" data-testid="rf-note" />
                <Button size="sm" onClick={ajouterNote} disabled={!note.trim() || busy} data-testid="rf-note-ajouter">{busy && <Loader2 size={13} className="animate-spin" />} Ajouter l'information</Button>
              </div>
            )}
            {f.notes.length === 0 ? <p className="text-sm text-muted-foreground">Aucune information ajoutée.</p> : (
              <ol className="relative border-l border-border ml-1.5 space-y-3" data-testid="rf-journal">
                {f.notes.map((n) => (
                  <li key={n.id} className="pl-4">
                    <span className={`absolute -left-1.5 w-3 h-3 rounded-full border-2 border-background ${n.type === "info" ? "bg-primary" : "bg-muted-foreground"}`} />
                    <p className={`text-sm whitespace-pre-line ${n.type === "info" ? "" : "text-muted-foreground"}`}>{n.texte}</p>
                    <p className="text-[11px] text-muted-foreground flex items-center gap-2">
                      {fmtDateHeure(n.date)} · {n.par?.nom || "—"}
                      {n.type === "info" && (user?.is_admin || n.par?.matricule === user?.matricule) && modifier && (
                        <button onClick={() => supprimerNote(n)} className="hover:text-destructive" title="Supprimer cette information"><Trash2 size={11} /></button>
                      )}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </aside>
        </div>
      )}
      <FicheForm open={edit} fiche={f} onClose={() => setEdit(false)} onSaved={(d) => { setEdit(false); setF(d); }} />
    </AppShell>
  );
}
